import React from 'react';
import { Tooltip, Box, Text, Flex } from 'pounceV2';
import NotificationJewelV3 from 'Components/NotificationJewel/NotificationJewelV3';

interface LimitItemDisplayProps {
  /**
   * How many items should we show before we limit them
   */
  limit: number;
  /**
   * The Component to render in place of the hidden items
   */
  AdditionalItemsComponent?: React.FC<{ additionalItems: LimitItemDisplayProps['children'][] }>;
  /**
   * Gap between items in the tooltip content
   */
  gap?: number;
  /**
   * @ignore
   */
  children: React.ReactNode | React.ReactNode[];
}

const LimitItemDisplayV3: React.FC<LimitItemDisplayProps> = ({
  limit,
  AdditionalItemsComponent = ({ additionalItems }) => (
    <NotificationJewelV3
      content={`+${additionalItems.length?.toLocaleString()}`}
      minWidth={16}
      height={16}
      width="auto"
    />
  ),
  gap = 1,
  children,
}) => {
  const childrenCount = React.Children.count(children);
  if (childrenCount <= limit) {
    return children as any;
  }

  const childrenList = React.Children.toArray(children);
  const displayedChildren = childrenList.slice(0, limit);
  const hiddenChildren = childrenList.slice(limit);
  return (
    <React.Fragment>
      {displayedChildren}
      <Tooltip
        disableInteractive={false}
        content={
          <Flex direction="column" gap={gap} maxHeight={300} overflow="auto">
            {hiddenChildren.map((c, index) => (
              <Text variant="body/sm" key={React.isValidElement(c) ? c.key : `hidden-${index}`}>
                {c}
              </Text>
            ))}
          </Flex>
        }
        placement="top"
      >
        <Box pointerEvents="auto" position="relative" display="flex" zIndex={1}>
          <AdditionalItemsComponent additionalItems={hiddenChildren} />
        </Box>
      </Tooltip>
    </React.Fragment>
  );
};

export default React.memo(LimitItemDisplayV3);
